import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { Spinner } from '@core/components';
import { XApp, XData } from '@core/utils';
import { Module } from '../XAppWrapper';

export interface RemoteEnum {
  enumName: string;
  id: string;
  name: string;
  nameSpace: string;
  value: number;
}

interface EnumGetterResult {
  data: RemoteEnum[];
  wrapperName: string;
}

type EnumGetter = () => Promise<EnumGetterResult>;

interface Enums {
  [wrappedName: string]: {
    [enumName: string]: RemoteEnum[];
  };
}

export const EnumsContext = createContext<{ enums: Enums }>({ enums: {} });

const getWrappedEnums = (data: RemoteEnum[]) => {
  const wrappedEnums = data.reduce<{ [key: string]: RemoteEnum[] | undefined }>(
    (acc, remoteEnum) => {
      const lowercaseRemoteEnum = XData.shallowLowercaseObj<RemoteEnum>(
        remoteEnum
      );
      const enumAlreadyAdded = acc[lowercaseRemoteEnum.enumName];

      if (enumAlreadyAdded) {
        return {
          ...acc,
          [lowercaseRemoteEnum.enumName]: [
            ...enumAlreadyAdded,
            lowercaseRemoteEnum
          ]
        };
      }

      return { ...acc, [lowercaseRemoteEnum.enumName]: [lowercaseRemoteEnum] };
    },
    {}
  );

  return wrappedEnums;
};

const createWithEnums = (enumGetters: EnumGetter[]) => (
  WrappedComponent: React.FC<unknown>
) => (props: any) => {
  const [enums, setEnums] = useState<Enums | null>(null);

  useEffect(() => {
    const enumGettersPromises = enumGetters.map((getEnums) => getEnums());

    Promise.all(enumGettersPromises).then((enumGettersResults) => {
      const namedEnums: Enums = enumGettersResults.reduce(
        (acc, { data, wrapperName }) => ({
          ...acc,
          [wrapperName]: getWrappedEnums(data)
        }),
        {}
      );

      setEnums(namedEnums);
    });
  }, []);

  if (!enums) {
    return <Spinner />;
  }

  return (
    <EnumsContext.Provider value={{ enums }}>
      <WrappedComponent {...props} />
    </EnumsContext.Provider>
  );
};

export const getEnumsModule = (modules: Module[]) => {
  const enumGetters: EnumGetter[] = modules
    .filter((x) => x.enumsEndpoint && x.id)
    .map((x) => {
      return () =>
        axios
          .get<RemoteEnum[]>(
            `${XApp.getApiUrlFromGlobal(x.apiConfigFields)}${
              x.enumsEndpoint as string
            }`
          )
          .then(({ data }) => ({ wrapperName: x.id as string, data }));
    });

  return {
    hoc: createWithEnums(enumGetters)
  };
};
